import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { toast } from 'react-toastify';
import CropRotateIcon from '@material-ui/icons/CropRotate';
import CropIcon from '@material-ui/icons/Crop';
import Clickable from '../../../components/clickable/Clickable';
import FormattedMessageWithClass from '../../../components/formattedMessageWithClass';
import styles from './attachments.module.scss';
import { docFileExtensions, imageFileExtensions, checkMultiFilesValidation, profileExtensions } from '../../../utils/helpers';
import Modal from '../../../components/modal';
import CropImage from '../../../components/cropImage/CropImage';
import Spinner from '../../../components/spinner';

const Attachments = ({ attachments, handleAnswerChanged, id: key, handleRemove, adpAttachments, profilePic,
  title, required: isRequired, handleFieldErrorChanged, newForm, isCrop, isRotate, handleAttachmentFailedAlert, myforms,
  showFieldErrors, disabled=false, handleRemoveCustomAttachment, handleAutoUploadAttachments, entityName, getLoadingStatus
}) => {
  const [openCropModal, setOpenCropModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [preImage, setPreImage] = useState(null);
  const [load, setLoad] = useState(false);
  const handleRemoveImage = (r) => {
    const res = attachments.filter(e => e !== r);
    if (adpAttachments) {
      handleRemove(r); // to remove from attachments when using req_attachments and photo
    }
    if (r.id && handleRemoveCustomAttachment) {
      handleRemoveCustomAttachment(r.id);
    }
    handleAnswerChanged(res, key);
    if (res.length === 0) handleFieldErrorChanged(key, isRequired);
  };

  const handleDrop = async (dropped) => {
    if (!checkMultiFilesValidation(dropped, isCrop ? profileExtensions :`${imageFileExtensions} , ${docFileExtensions}`)) return;
    const list = attachments || [];
    handleAnswerChanged([...list, ...dropped], key);
    handleFieldErrorChanged(key, false);
  };

    const handleAutoDrop = async (dropped) => {
    const validExtensions = isCrop ? profileExtensions : `${imageFileExtensions}, ${docFileExtensions}`;
    if (!checkMultiFilesValidation(dropped, validExtensions)) return;
    const list = attachments || [];
    setLoad(true);
    if (getLoadingStatus) getLoadingStatus(true);
    try {
      let updatedAttachments = [];
      if (handleAutoUploadAttachments) {
        try {
          const attachmentIds = await handleAutoUploadAttachments(dropped, entityName);
          if (!attachmentIds.length) {
            if (typeof handleAttachmentFailedAlert === 'function') {
              handleAttachmentFailedAlert(true);
            }
            return;
          }
          updatedAttachments = dropped.map((file, i) => ({
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            preview: file.preview,
            attachment_id: attachmentIds[i],
            rawFile: file
          }));
        } catch (uploadError) {
          toast.error('Failed to upload some files. Please try again.');
          return; // Stop processing further
        }
      }
      handleAnswerChanged([...list, ...updatedAttachments], key);
      handleFieldErrorChanged(key, false);
    } catch (error) {
      toast.error('An unexpected error occurred while processing your files. Please try again.');
    } finally {
      setLoad(false);
      if (getLoadingStatus) getLoadingStatus(false);
    }
  };

  // Handle file input change
  const handleCrop = (image) => {
    if (image?.rawFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result); // Set the image as base64
      };
      reader.readAsDataURL(image?.rawFile);
      setPreImage(image?.rawFile);
    }
    setOpenCropModal(true);
  };

  const handleCroppedImage = async (croppedFile) => {
    if (handleAutoUploadAttachments) {
      setLoad(true);
      try {
        const updatedAttachments = await Promise.all(
          attachments.map(async (file) => {
            if (file?.rawFile === preImage) {
              const attachmentIds = await handleAutoUploadAttachments([croppedFile], entityName);
              return {
                name: croppedFile.name,
                type: croppedFile.type,
                size: croppedFile.size,
                lastModified: croppedFile.lastModified,
                preview: croppedFile.preview,
                attachment_id: attachmentIds[0],
                rawFile: croppedFile
              };
            }
            return file;
          })
        );
        handleAnswerChanged(updatedAttachments, key);
      } catch (error) {
        toast.error('Error uploading cropped image');
      } finally {
        setLoad(false);
        closeCropModal();
      }
    } else {
      const updatedAttachments = attachments.map(file => ((file === preImage) ? croppedFile : file));
      handleAnswerChanged(updatedAttachments, key);
      closeCropModal();
    }
  };

  const closeCropModal = () => {
    setOpenCropModal(false);
    setSelectedImage(null);
    setPreImage(null);
  };

  return (
  <>
    <div className={styles.fullInput}>
      { isRequired && <small style={{ color: 'red' }}> * </small> }
      <FormattedMessageWithClass
        className={styles.label} id={title}
        defaultMessage={title}
      />

      <Dropzone
        onDrop={handleAutoUploadAttachments ? handleAutoDrop : handleDrop}
        disableClick
        className={newForm ? `${styles.dropzone1} ${styles.dropzone}` : styles.dropzone} accept="image/*, .pdf, .doc"
        disabled={disabled || load}
      >
        {({ open }) => (
            <p className={styles.browsetext}>
              <FormattedMessage id="airport.logo.text1" defaultMessage="Drag the attachment here " />
              <button type="button" onClick={() => open()}>
                <FormattedMessage id="airport.logo.button" defaultMessage="browse" />
              </button>
              <FormattedMessage id="airport.logo.text2" defaultMessage=" to upload." />
            </p>
        )}
      </Dropzone>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {profilePic && (
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            <FormattedMessage
            id="documents.upload.file.allowed_types"
            defaultMessage="Note: You can upload images in .jpg, .jpeg, or .png format."
          />
          </span>
          )}
      {showFieldErrors && isRequired && (
       <small style={{ color: 'red', marginTop: '4px' }}>
      {(!attachments || attachments?.length === 0) && (
        <FormattedMessage
          id="pulpoforms.errors.not_blank"
          defaultMessage="This field cannot be empty"
        />
      )}
       </small>
      )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {attachments?.length > 0 && (
          <div className={styles.photos}>
            {attachments.map(e => (
              <div key={e.name} className={styles.wrapper}>
                <Clickable onClick={() => handleRemoveImage(e)}>&times;</Clickable>
                <img src={e.preview || e.url} alt={myforms ? (e.file_name || e.name) : (e.name || e.file_name)} />
                {isCrop && e.type?.startsWith('image/') && (
                <button className={styles.cropBtn} onClick={() => handleCrop(e)}>
                  {isRotate ? <CropRotateIcon /> : <CropIcon />}
                </button>
                )}
              </div>
            ))}
          </div>
        )}
        <Spinner active={load} large />
      </div>
    </div>
    <Modal
      showIn={openCropModal}
      onClose={() => {}}
      width="864px"
      minHeight="fit-content"
    >
      <div className={styles.cropModal}>
        <FormattedMessage
          id="edit.attachment"
          defaultMessage="Edit Attachment"
          tagName="h3"
        />
        <div
          className={styles.closeIcon}
          onClick={closeCropModal}
          onKeyPress={closeCropModal}
          role="button"
          tabIndex={0}
        >
          &times;
        </div>
      </div>
      <CropImage
        image={selectedImage}
        selectedFile={preImage}
        handleSaveCropImage={handleCroppedImage}
        isRotate={isRotate}
        onClose={closeCropModal}
        isLoading={load}
      />
    </Modal>
  </>
  );
};

Attachments.propTypes = {
  handleAnswerChanged: PropTypes.func.isRequired,
  attachments: PropTypes.shape([]),
  id: PropTypes.string.isRequired
};

Attachments.defaultProps = {
  attachments: []
};

export default Attachments;
